import { activityOfState, asId, type AvatarColor, type UserId, type UserRef } from '@cyphex/types';

const TINTS: readonly AvatarColor[] = ['teal', 'pink', 'blue', 'orange', 'green', 'indigo', 'violet'];

/** Mock identities. Avatar tint is wire data; components derive their own. */
function person(
  seed: string,
  displayName: string,
  username: string,
  jobTitle: string,
  presence: UserRef['presence'],
  isExternal = false,
): UserRef {
  const initials = displayName
    .split(' ')
    .map((part) => part[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return {
    id: asId<UserId>(`00000000-0000-4000-8000-0000000000${seed}`),
    displayName,
    username,
    type: 'member',
    initials,
    avatarColor: TINTS[Number(seed) % TINTS.length] ?? 'slate',
    avatarUrl: null,
    jobTitle,
    presence,
    isExternal,
  };
}

const now = new Date().toISOString();
const summary = (state: NonNullable<UserRef['presence']>['state'], statusLine?: string) => ({
  state,
  since: now,
  statusLine: statusLine ?? null,
  /* Derived, never written per row: `in_a_meeting` IS an activity, and a fixture
     that said otherwise would be the one place left where the client's resolver
     still saw the pre-fix shape — a state with no activity behind it. */
  activityState: activityOfState(state),
});

export const ME = person('01', 'Vamshi Uppala', 'vamshi', 'Founder', summary('available'));

export const PEOPLE: readonly UserRef[] = [
  person('02', 'Rohit Sharma', 'rohit', 'Engineering Lead', summary('available', 'Working on Project Phoenix')),
  person('03', 'Anjali Mehta', 'anjali', 'Product Designer', summary('in_a_meeting', 'Design sync')),
  person('04', 'Vikram Reddy', 'vikram', 'DevOps Engineer', summary('available')),
  person('05', 'Serena Davis', 'serena', 'Head of Marketing', summary('away')),
  person('06', 'Babak Shammas', 'babak', 'Solutions Architect', summary('do_not_disturb', 'Heads down until 4')),
  person('07', 'Audi Kapoor', 'audi', 'QA Engineer', summary('available')),
  person('08', 'Charlotte De Crum', 'charlotte', 'Product Lead', summary('be_right_back')),
  person('09', 'Miguel Santos', 'miguel', 'Partner Success', summary('offline'), true),
  person('10', 'Priya Nair', 'priya', 'Data Analyst', summary('out_of_office', 'Back Monday')),
];

export const PEOPLE_BY_ID: ReadonlyMap<string, UserRef> = new Map(
  [ME, ...PEOPLE].map((user) => [user.id, user]),
);

export function personAt(index: number): UserRef {
  return PEOPLE[index % PEOPLE.length] ?? ME;
}
